import { zodResolver } from '@hookform/resolvers/zod'
import { CalendarClock, Loader2 } from 'lucide-react'
import { Controller, useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { z } from 'zod'
import { CheckRow, Field, FormSection } from '@/components/common/FormBits'
import { Button } from '@/components/ui/button'
import { DateField } from '@/components/ui/date-field'
import { Input } from '@/components/ui/input'
import { Modal } from '@/components/ui/modal'
import { FormSelect } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { TimeField } from '@/components/ui/time-field'
import { useContacts } from '@/features/contacts/api'
import { useSaveMeeting } from '../../api'
import { ChipsPicker } from './ChipsPicker'
import { CHANNELS, MEETING_VISIBILITIES, RESULTS, type Meeting } from '../../types'
import { toastError, useCrm } from '../../ui'

type Props = { customerId: string; meeting: Meeting | null; plan?: boolean; onDone: (id?: string) => void }

/** Görüşme kaydı ya da (plan=true) ileri tarihli ziyaret planı. */
export function MeetingForm({ customerId, meeting, plan, onDone }: Props) {
  const { t } = useTranslation()
  const save = useSaveMeeting()
  const { staff } = useCrm()
  const contacts = useContacts(customerId)
  const today = new Date().toISOString().slice(0, 10)
  const schema = z
    .object({
      date: z.string().min(1, t('crm.form.dateRequired')),
      start: z.string().min(1, t('crm.form.timeRequired')),
      end: z.string(),
      staffIds: z.array(z.string()).min(1, t('crm.form.staffRequired')),
      contactIds: z.array(z.string()),
      customerResponse: z.string().trim().max(2000),
      decisions: z.string().trim().max(2000),
      channel: z.enum(CHANNELS),
      visibility: z.enum(MEETING_VISIBILITIES),
      result: z.enum(RESULTS),
      summary: z.string().trim().min(3, t('crm.form.summaryRequired')).max(1000),
      hasNext: z.boolean(),
      nextDate: z.string(),
      nextStaffId: z.string(),
      nextText: z.string().trim().max(200),
    })
    .refine((v) => !v.end || v.end >= v.start, { path: ['end'], message: t('errors.TIME_RANGE_INVALID') })
    .refine((v) => !v.hasNext || (v.nextDate && v.nextText.length >= 3), { path: ['nextText'], message: t('crm.form.nextRequired') })
  const form = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      date: meeting?.date ?? today,
      start: meeting?.start ?? '10:00',
      end: meeting?.end ?? '',
      staffIds: meeting?.staffIds.length ? meeting.staffIds : meeting?.staffId ? [meeting.staffId] : staff[0]?.id ? [staff[0].id] : [],
      contactIds: meeting?.contactIds ?? [],
      customerResponse: meeting?.customerResponse ?? '',
      decisions: meeting?.decisions ?? '',
      channel: meeting?.channel ?? (plan ? 'visit' : 'phone'),
      visibility: meeting?.visibility ?? 'restricted',
      result: meeting?.result ?? 'reached',
      summary: meeting?.summary ?? (plan ? t('crm.meeting.plannedSummary') : ''),
      hasNext: !!meeting?.nextAction,
      nextDate: meeting?.nextAction?.date ?? '',
      nextStaffId: meeting?.nextAction?.staffId ?? '',
      nextText: meeting?.nextAction?.text ?? '',
    },
  })
  const { errors } = form.formState
  const hasNext = useWatch({ control: form.control, name: 'hasNext' })

  const onSubmit = form.handleSubmit(async ({ hasNext: hn, nextDate, nextStaffId, nextText, ...v }) => {
    try {
      const staffId = v.staffIds[0] ?? ''
      const id = await save.mutateAsync({ id: meeting?.id, input: { ...v, staffId, customerId, nextAction: hn ? { date: nextDate, staffId: nextStaffId || staffId, text: nextText } : null } })
      toast.success(t(meeting ? 'crm.meeting.updated' : plan ? 'crm.meeting.planned' : 'crm.meeting.created'))
      onDone(id)
    } catch (e) {
      toastError(e)
    }
  })
  const staffOptions = staff.map((s) => ({ value: s.id, label: s.name, description: s.title }))

  return (
    <Modal
      open
      size="lg"
      onOpenChange={(v) => !v && onDone()}
      dismissible={!save.isPending}
      icon={<CalendarClock />}
      title={t(meeting ? 'crm.meeting.editTitle' : plan ? 'crm.meeting.planTitle' : 'crm.meeting.createTitle')}
      description={t('crm.meeting.hint')}
      onSubmit={(e) => void onSubmit(e)}
      footer={
        <>
          <Button type="button" variant="cancel" onClick={() => onDone()}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" disabled={save.isPending}>
            {save.isPending ? <Loader2 className="animate-spin" /> : null}
            {t('common.save')}
          </Button>
        </>
      }
    >
      <div className="space-y-6">
        <FormSection title={t('crm.meeting.sectionMeeting')}>
          <Field id="mt-date" label={t('crm.fields.date')} error={errors.date?.message}>
            <Controller control={form.control} name="date" render={({ field }) => <DateField id="mt-date" value={field.value} onChange={field.onChange} clearable={false} />} />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field id="mt-start" label={t('crm.fields.start')} error={errors.start?.message}>
              <Controller control={form.control} name="start" render={({ field }) => <TimeField id="mt-start" value={field.value} onChange={field.onChange} />} />
            </Field>
            <Field id="mt-end" label={t('crm.fields.end')} error={errors.end?.message}>
              <Controller control={form.control} name="end" render={({ field }) => <TimeField id="mt-end" value={field.value} onChange={field.onChange} />} />
            </Field>
          </div>
          <Field id="mt-staff" label={t('crm.meeting.participants')} hint={t('crm.meeting.participantsHint')} error={errors.staffIds?.message} full>
            <Controller control={form.control} name="staffIds" render={({ field }) => <ChipsPicker id="mt-staff" value={field.value} onChange={field.onChange} options={staffOptions} />} />
          </Field>
          <Field id="mt-contacts" label={t('crm.meeting.customerAttendees')} full>
            <Controller
              control={form.control}
              name="contactIds"
              render={({ field }) => (
                <ChipsPicker id="mt-contacts" value={field.value} onChange={field.onChange} empty={t('crm.meeting.noContacts')} options={(contacts.data ?? []).map((c) => ({ value: c.id, label: c.title ? `${c.full_name} · ${c.title}` : c.full_name }))} />
              )}
            />
          </Field>
          <Field id="mt-channel" label={t('crm.fields.channel')}>
            <FormSelect id="mt-channel" control={form.control} name="channel" options={CHANNELS.map((c) => ({ value: c, label: t(`crm.channel.${c}`) }))} />
          </Field>
          <Field id="mt-visibility" label={t('crm.meeting.visibility')} hint={t('crm.meeting.visibilityHint')}>
            <FormSelect id="mt-visibility" control={form.control} name="visibility" options={MEETING_VISIBILITIES.map((v) => ({ value: v, label: t(`crm.meeting.visibilities.${v}`) }))} />
          </Field>
          <Field id="mt-result" label={t('crm.fields.result')} full>
            <FormSelect id="mt-result" control={form.control} name="result" options={RESULTS.map((r) => ({ value: r, label: t(`crm.result.${r}`) }))} />
          </Field>
          <Field id="mt-summary" label={t('crm.fields.summary')} error={errors.summary?.message} full>
            <Textarea id="mt-summary" rows={4} maxLength={1000} aria-invalid={!!errors.summary} {...form.register('summary')} />
          </Field>
          <Field id="mt-response" label={t('crm.meeting.customerResponse')} full>
            <Textarea id="mt-response" rows={3} maxLength={2000} placeholder={t('crm.meeting.customerResponsePlaceholder')} {...form.register('customerResponse')} />
          </Field>
          <Field id="mt-decisions" label={t('crm.meeting.decisions')} full>
            <Textarea id="mt-decisions" rows={3} maxLength={2000} placeholder={t('crm.meeting.decisionsPlaceholder')} {...form.register('decisions')} />
          </Field>
        </FormSection>
        <FormSection title={t('crm.meeting.sectionNext')}>
          <CheckRow {...form.register('hasNext')}>{t('crm.meeting.hasNext')}</CheckRow>
          {hasNext ? (
            <>
              <Field id="mt-ndate" label={t('crm.fields.date')}>
                <Controller control={form.control} name="nextDate" render={({ field }) => <DateField id="mt-ndate" value={field.value} onChange={field.onChange} />} />
              </Field>
              <Field id="mt-nstaff" label={t('crm.fields.staff')}>
                <FormSelect id="mt-nstaff" control={form.control} name="nextStaffId" placeholder={t('crm.meeting.sameStaff')} options={staffOptions} />
              </Field>
              <Field id="mt-ntext" label={t('crm.fields.nextText')} error={errors.nextText?.message} full>
                <Input id="mt-ntext" maxLength={200} {...form.register('nextText')} />
              </Field>
            </>
          ) : null}
        </FormSection>
      </div>
    </Modal>
  )
}
